import React, { useState, useEffect, useCallback, useMemo } from "react";
import { permissionProfileService } from "../../services/permissionProfile.service";

const EditPermissionProfileModal = ({ show, onHide, id, onSave }) => {
  const [selectedMenuIds, setSelectedMenuIds] = useState([]);
  const [groupedMenus, setGroupedMenus] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!show || !id) return;

    setLoading(true);
    permissionProfileService
      .profileDetailsUnAssign(id)
      .then((res) => {
        const menus = res.data.profile_menu_info || [];

        const initialSelected = menus
          .filter((m) => m.permission_status === "true")
          .map((m) => m.menu_unique_id);
        setSelectedMenuIds(initialSelected);

        const grouped = menus.reduce((acc, menu) => {
          const parent = menu.menu_parent_name || "Other";
          if (!acc[parent]) acc[parent] = [];
          acc[parent].push(menu);
          return acc;
        }, {});
        setGroupedMenus(grouped);
      })
      .catch((err) => console.error("unassign error", err))
      .finally(() => setLoading(false));
  }, [show, id]);

  useEffect(() => {
    document.body.style.overflow = show ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [show]);

  const handleCheckboxChange = useCallback((menuUniqueId, checked) => {
    setSelectedMenuIds((prev) =>
      checked
        ? prev.includes(menuUniqueId) ? prev : [...prev, menuUniqueId]
        : prev.filter((id) => id !== menuUniqueId)
    );
  }, []);

  const handleGroupChange = useCallback((menus, checked) => {
    const groupIds = menus.map((m) => m.menu_unique_id);
    setSelectedMenuIds((prev) =>
      checked
        ? [...new Set([...prev, ...groupIds])]
        : prev.filter((id) => !groupIds.includes(id))
    );
  }, []);

  // NEW: Global Select All / Deselect All handlers
  const handleSelectAll = useCallback(() => {
    const allIds = Object.values(groupedMenus).flat().map((m) => m.menu_unique_id);
    setSelectedMenuIds(allIds);
  }, [groupedMenus]);

  const handleDeselectAll = useCallback(() => {
    setSelectedMenuIds([]);
  }, []);

  // NEW: Check if every menu across all groups is selected
  const isAllSelected = useMemo(() => {
    const allMenus = Object.values(groupedMenus).flat();
    if (allMenus.length === 0) return false;
    return allMenus.every((menu) => selectedMenuIds.includes(menu.menu_unique_id));
  }, [groupedMenus, selectedMenuIds]);

  const getGroupState = (menus) => {
    const groupIds = menus.map((m) => m.menu_unique_id);
    const checkedCount = groupIds.filter((id) => selectedMenuIds.includes(id)).length;
    if (checkedCount === 0) return { checked: false, indeterminate: false };
    if (checkedCount === groupIds.length) return { checked: true, indeterminate: false };
    return { checked: false, indeterminate: true };
  };

  const handleSave = () => onSave(selectedMenuIds);

  if (!show) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={onHide}
    >
      <div
        className="relative bg-white rounded-xl shadow-2xl w-full max-w-2xl mx-4 flex flex-col max-h-[90vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header - NOW CONTAINS THE "Check All / Uncheck All" BUTTON */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-xl font-semibold text-gray-800">Edit Permission Menus</h2>

          <div className="flex items-center gap-3">
            {/* NEW BUTTON: placed at the top (in header) - always visible when data is loaded */}
            {Object.keys(groupedMenus).length > 0 && (
              <button
                onClick={isAllSelected ? handleDeselectAll : handleSelectAll}
                className="px-4 py-1.5 text-sm font-medium rounded-lg border border-blue-200 hover:border-blue-300 hover:bg-blue-50 text-blue-600 transition-colors flex items-center gap-1.5"
              >
                {isAllSelected ? (
                  <>
                    {/* Uncheck icon (simple SVG) */}
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6h12v12" />
                    </svg>
                    <span>Uncheck All</span>
                  </>
                ) : (
                  <>
                    {/* Check icon (simple SVG) */}
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7" />
                    </svg>
                    <span>Check All</span>
                  </>
                )}
              </button>
            )}

            <button onClick={onHide} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">
              &times;
            </button>
          </div>
        </div>

        {/* Scrollable body */}
        <div className="overflow-y-auto flex-1 px-0">
          {loading ? (
            <div className="px-6 py-12 text-center text-gray-500">Loading...</div>
          ) : Object.keys(groupedMenus).length > 0 ? (
            Object.entries(groupedMenus).map(([parentName, menus]) => {
              const { checked, indeterminate } = getGroupState(menus);
              return (
                <div key={parentName + menus[0].menu_unique_id} className="border-b last:border-b-0">
                  {/* Parent Header with Checkbox */}
                  <label className="flex items-center gap-3 bg-gray-100 px-6 py-3 sticky top-0 z-10 border-b cursor-pointer hover:bg-gray-200 transition-colors">
                    <input
                      type="checkbox"
                      checked={checked}
                      ref={(el) => { if (el) el.indeterminate = indeterminate; }}
                      onChange={(e) => handleGroupChange(menus, e.target.checked)}
                      className="w-4 h-4 accent-blue-600 cursor-pointer"
                    />
                    <span className="font-semibold text-gray-700">{parentName}</span>
                    <span className="ml-auto text-xs text-gray-400 font-normal">
                      {menus.filter((m) => selectedMenuIds.includes(m.menu_unique_id)).length}/{menus.length}
                    </span>
                  </label>

                  {/* Child Menus */}
                  <div className="divide-y">
                    {menus.map((menu) => {
                      const isChecked = selectedMenuIds.includes(menu.menu_unique_id);
                      return (
                        <label
                          key={menu.menu_unique_id}
                          className="flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors cursor-pointer"
                        >
                          <div className="flex items-center gap-3">
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={(e) => handleCheckboxChange(menu.menu_unique_id, e.target.checked)}
                              className="w-4 h-4 accent-blue-600 cursor-pointer"
                            />
                            <span className="text-base font-medium text-gray-700">{menu.menu_name}</span>
                          </div>
                          <span className="text-xs text-gray-400 font-mono">{menu.module_name}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              );
            })
          ) : (
            <div className="px-6 py-12 text-center text-gray-500">No menu data available to edit</div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-6 py-4 border-t bg-gray-50 rounded-b-xl">
          <button
            onClick={onHide}
            className="px-5 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-6 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors"
          >
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditPermissionProfileModal;